import {
  CloseCircleOutlined,
  CheckCircleOutlined,
  ClearOutlined,
  CheckOutlined,
  UploadOutlined,
  SearchOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { Tooltip, Input, Button, Tag, Divider, Empty } from 'antd';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import VirtualizedList from '@components/VirtualizedList';
import { useTranslation } from 'react-i18next';
import SearchUploaderModal from '@components/SearchUploader/SearchUploaderModal';
import SessionContext from '@providers/Session/SessionContext';

import * as S from './styles';

interface IItem {
  label: string;
  value: number;
}

interface IListProps {
  items: IItem[] | undefined;
  preSelectedItems: IItem[] | undefined;
  columnName: string;
  onSelectedItemsChange: (values: IItem[]) => void;
  uploadAlternateTitle?: string;
}

const SuperList = ({
  items,
  preSelectedItems,
  columnName,
  onSelectedItemsChange,
  uploadAlternateTitle,
}: IListProps) => {
  const { t } = useTranslation('super-list');
  const { theme } = useContext(SessionContext);

  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [isUploadModalVisible, setIsUploadModalVisible] =
    useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>();

  useEffect(() => {
    if (preSelectedItems) {
      setSelectedItems(preSelectedItems);
    }
  }, [preSelectedItems]);

  useEffect(() => {
    onSelectedItemsChange(selectedItems);
  }, [selectedItems, onSelectedItemsChange]);

  const searchResultItems = useMemo(() => {
    if (searchValue) {
      const resultsBySearch = items?.filter(item => {
        return item.label.match(new RegExp(searchValue, 'i'));
      });
      // remove already selected items
      return resultsBySearch?.filter(
        result => !selectedItems.find(item => item?.value === result.value),
      );
    }
    return undefined;
  }, [items, searchValue, selectedItems]);

  const removeSelectedItem = useCallback(
    (value: number) =>
      setSelectedItems(oldItems =>
        oldItems?.filter(item => item?.value !== value),
      ),
    [],
  );

  const addSelectedItem = useCallback(
    (value: number) => {
      const itemToAdd = items?.find(item => item.value === Number(value));
      setSelectedItems(oldItems => [...oldItems, itemToAdd]);
    },
    [items],
  );

  const addSentItemsList = useCallback(
    (list: any[]) => {
      const itemsByUpload = list
        .map(listItem =>
          items?.find(
            // @ts-ignore
            item => String(item[columnName]) === listItem[columnName],
          ),
        )
        ?.filter(listItem => listItem)
        ?.filter(
          listItem =>
            !selectedItems.find(item => item?.value === listItem?.value),
        );
      setSelectedItems([...selectedItems, ...itemsByUpload]);
    },
    [items, selectedItems, columnName],
  );

  const ItemEl = useCallback(
    ({ value, label, isSelected }: any) => (
      <Tag
        style={{
          borderRadius: 4,
          border: `1px solid ${theme?.colors?.border}`,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Tooltip title={isSelected ? t('buttons.remove') : t('buttons.select')}>
          <Button
            type="link"
            icon={
              isSelected ? <CloseCircleOutlined /> : <CheckCircleOutlined />
            }
            style={{
              minWidth: 40,
            }}
            onClick={() => {
              if (isSelected) {
                removeSelectedItem(value);
              } else {
                addSelectedItem(value);
              }
            }}
          />
        </Tooltip>
        <p style={{ margin: 0 }}>{`${label} - ${value}`}</p>
      </Tag>
    ),
    [theme, t, removeSelectedItem, addSelectedItem],
  );

  return (
    <S.SuperListContainer>
      {items?.length !== selectedItems?.length && (
        <>
          {items && (
            <div style={{ display: 'flex' }}>
              <Tooltip title={t('buttons.select-all')}>
                <Button
                  icon={<CheckOutlined />}
                  style={{ marginRight: 8 }}
                  disabled={items?.length === selectedItems?.length}
                  onClick={() => {
                    if (items) {
                      setSelectedItems(items);
                    }
                    setSearchValue(undefined);
                  }}
                />
              </Tooltip>
              <Input
                suffix={
                  <Tooltip title={t('upload.tooltip')}>
                    <Button
                      type="link"
                      icon={<UploadOutlined />}
                      size="small"
                      onClick={() => {
                        setIsUploadModalVisible(true);
                      }}
                    />
                  </Tooltip>
                }
                onChange={({ target: { value } }) => setSearchValue(value)}
                value={searchValue}
                disabled={!items}
                allowClear
              />
            </div>
          )}
          <div
            style={{
              height: items ? 160 : 'auto',
              marginTop: 4,
            }}
          >
            <VirtualizedList listItems={searchResultItems} itemEl={ItemEl} />
            {items ? (
              <>
                {searchResultItems && searchResultItems.length === 0 && (
                  <div className="search-not-found">
                    <Empty
                      description={t('search.not-found')}
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  </div>
                )}
                {!searchValue && (
                  <div className="search-description">
                    <SearchOutlined className="search-description-icon" />
                    <p className="search-description-text">
                      {t('search.description')}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="search-loading">
                <LoadingOutlined className="search-loading-icon" />
                <p>{t('search.loading')}</p>
              </div>
            )}
          </div>
        </>
      )}

      {selectedItems && selectedItems.length > 0 && (
        <div style={{ height: '100%' }}>
          <Divider className="selected-items-title">
            {t('selected-title')}
          </Divider>
          <div style={{ height: selectedItems.length * 40, maxHeight: 160 }}>
            <VirtualizedList
              listItems={selectedItems}
              itemEl={ItemEl}
              itemIsSelected
            />
          </div>
        </div>
      )}
      <footer>
        <span>
          {selectedItems?.length > 0 &&
            t('selected', { count: selectedItems.length })}
        </span>

        <div style={{ textAlign: 'right' }}>
          {selectedItems?.length > 0 && (
            <Tooltip title={t('buttons.clear')}>
              <Button
                icon={<ClearOutlined />}
                className="button-clear"
                onClick={() => setSelectedItems([])}
              />
            </Tooltip>
          )}
        </div>
      </footer>
      <SearchUploaderModal
        visible={isUploadModalVisible}
        onClose={() => setIsUploadModalVisible(false)}
        columnName={columnName}
        alternateTitle={uploadAlternateTitle}
        infoModalWidth={480}
        validation={false}
        description={t('upload.description')}
        setList={addSentItemsList}
      />
    </S.SuperListContainer>
  );
};

export default React.memo(SuperList);
